/* eslint-disable max-lines */
import React from 'react';
import { Grid } from '../../../../../kit/Grid';
import {
  HSAdministeredDrug,
  HSDoseRound,
  HSDrug,
  HSPackedMedication,
  HSPackedPatientDay,
  HSPatchObservation, HSPatient, PatchApplicationDto, PatchApplicationScheduledActivityStatusDto,
  ReasonCode,
} from 'server-openapi';
import {
  MedicationInformationBox
} from '../../medicationInformation/MedicationInformationBox';
import { DateUtils, Interval } from '../../../../../core/utils/dateUtils';
import { CategoryHeading } from './ContinuousMedicationList';
import itiriri from 'itiriri';
import { useSyncCenter } from '../../../../../syncstream/SyncCenterProvider';
import { useStore } from '../../../../../core/storage/hooks/UseStore';
import { MedicationListProps } from '../../../components/MedicationListsTabbedRouter/MedicationListsTabbedRouter';
import { useApiUtils } from '../../../../../syncstream/utils/hooks/useApiUtils';
import { DrugUtils, PreviousDosedDrugs } from '../../../../../syncstream/utils/DrugUtils';
import { PatchLocations, PatchLocationDTO } from '../../patches/PatchLocations';
import { PatchUtils } from '../../patches/PatchUtils';
import { format, intervalToDuration } from 'date-fns';
import { UserUtils } from '../../../../../syncstream/utils/UserUtils';
import { MedicationGroup } from '../../../../../syncstream/utils/ResidentDetailsUtils';
import {RoundUtils } from '../../../../../syncstream/utils/RoundUtils';
import { EnqueuedDrugCreateData } from '../../../../../syncstream/SyncRounds';
import {getPatchApplicationAppliedText} from "../../../../../syncstream/utils/PatchApplicationUtils";

interface Props extends MedicationListProps {
  onAdministerDrugs: (drugs: HSAdministeredDrug[], doseTimestamp: string) => Promise<EnqueuedDrugCreateData | undefined>;
  groupedPackedMedicationList: MedicationGroup[];
  patientId: number;
  displayAllActions?: boolean;
}

export enum PatchStatus {
  Applied = 'Applied',
  ToSight = 'Sight',
  Sighted = 'Sighted',
  Removed = 'Removed',
  FallenOff = 'FallenOff',
  Reapplied = 'Reapplied',
}

export function getPatchStatusText(patchStatus: PatchStatus) {
  switch (patchStatus) {
    case PatchStatus.Applied:
      return 'Administered';
    case PatchStatus.ToSight:
      return 'Sight';
    case PatchStatus.Sighted:
      return 'Sighted';
    case PatchStatus.Removed:
      return 'Removed';
    case PatchStatus.FallenOff:
      return 'Fallen off';
    case PatchStatus.Reapplied:
      return 'Reapplied';
    default:
      return '';
  }
}

export interface PatchInfo {
  PatchStatus?: PatchStatus;
  ActionedDate?: string;
  ActionedBy?: string;
  TimeRemaining?: string;
  PatchApplication?: PatchApplicationDto;
  IsNew: boolean;
}

// eslint-disable-next-line max-lines-per-function
export function PatchesMedicationList(props: Props) {
  const services = useSyncCenter();
  const roundsStore = useStore(services.rounds.store).store;
  const patchApplicationsStore = useStore(services.patchApplications.store).store;
  const packedPatientDaysStore = useStore(services.packedPatientDays.store).store;
  const drugStore = useStore(services.drugs.store).store;
  const apiUtils = useApiUtils();

  const profiles = props.patient.patientProfiles ?? [];

  const roundWindow = props.currentRound?.createdAt
    ? apiUtils.rounds.getRoundWindow(DateUtils.toDate(props.currentRound.createdAt), props.facilityGroupId)
    : undefined;

  const isInRound = (grp: MedicationGroup, interval: Interval | undefined) => {
    if (!interval) {
      return true;
    }
    if (!grp.patchApplication?.hsId) {
      // It hasn't gone to the server yet which means it is an application which means that we can use the original date.
      return grp.categories.doseDate! >= DateUtils.toDate(interval.start) && grp.categories.doseDate! <= DateUtils.toDate(interval.end);
    }
    if (grp.medications.some(x => x.medicationId === grp.patchApplication?.lastAppliedPatchDrugAdministration?.medicationId)) {
      return true;
    }

    for(let scheduledActivity of grp.patchApplication?.scheduledActivities ?? []) {
      if (scheduledActivity.status === PatchApplicationScheduledActivityStatusDto.Pending && DateUtils.toDate(scheduledActivity.scheduledAt!) >= DateUtils.toDate(interval.start) && DateUtils.toDate(scheduledActivity.scheduledAt!) <= DateUtils.toDate(interval.end)) {
        return true;
      }
    } 
    return false;
  }
  
  const patches = props.groupedPackedMedicationList
    .filter(
      (group, index) =>
        group.categories.isPatch &&
        (props.displayAllActions || isInRound(group, roundWindow))
    )
    .sort((lhs, rhs) => DateUtils.compareDatesDescending(lhs.categories.doseDate!, rhs.categories.doseDate!, true));

  const patientPatchApplications = itiriri(patchApplicationsStore.values())
      .filter((p) => p.patientId === props.patientId && p.active!)
      .toArray();
  const packedPatientDays = itiriri(packedPatientDaysStore.values())
    .filter((p) => p.patientId === props.patientId && !!p.packDate)
    .map((day) => ({
      ...day,
      packedMedications: day.packedMedications?.filter((packedMed) =>
        profiles.some(
          (profile) =>
            [...(profile.allCurrentMedications ?? []), ...(profile.recentlyDeletedMedications ?? [])].find(
              (med) => med.hsId === packedMed.medicationId,
            )?.showOnSigningSheet,
        ),
      ),
    }))
    .toArray();
  
  return (
    <Grid cols={1} gap={1}>
      {patches.map((medicationGroup, index) => (
        <Grid cols={1} gap={0.5} key={index}>
          <CategoryHeading weight={'bold'}>
            {apiUtils.residentDetails.getMedicationHeader(medicationGroup, true)}
          </CategoryHeading>
          {medicationGroup.medications.map((packedMedication) => {
            const currentDosedDrug = apiUtils.rounds.getMostRecentDrugInRoundByPackedMedication(
              packedMedication,
              props.patient.hsId!,
              props.currentRound,
            );
            const status = apiUtils.rounds.getPackedMedicationStatus(packedMedication, props.patientId);
            
            return (
                <PatchMedicationInformation
                  key={index}
                  packedMedication={packedMedication}
                  currentDosedDrug={currentDosedDrug}
                  onAdminister={props.onAdministerDrugs}
                  status={status}
                  patchApplication={medicationGroup.patchApplication}
                  userUtils={apiUtils.users}
                  patchInfo={getPatchInfo(
                      apiUtils.rounds,
                      packedMedication,
                      packedPatientDays,
                      drugStore,
                      patientPatchApplications,
                      status
                  )}
                  roundsStore={roundsStore}
                  {...(props as MedicationListProps)}
                    displayAllActivity={props.displayAllActions}
                />
            );
          })}
        </Grid>
      ))}
    </Grid>
  );
}
function getPatchInfo(
    roundUtils: RoundUtils,
    packedMedication: HSPackedMedication,
    packedPatientDays: HSPackedPatientDay[],
    drugs: ReadonlyMap<string, HSDrug>,
    patientPatchApplications: PatchApplicationDto[],
    status?: ReasonCode,
): PatchInfo | undefined {

  var administeredDrugs = roundUtils.getAdministeredDrugsForPackedMedication(packedMedication, packedMedication.patientId!);
  var patchApplication = patientPatchApplications.find(x => administeredDrugs.some(y => y.clinicalSystemId === x.lastAppliedPatchDrugAdministration?.administeredDrugClinicalSystemId));
  if (patchApplication) {
    return {
      PatchApplication: patchApplication,
      IsNew: false
    };
  }
  const hasPatchBeenApplied = status === ReasonCode.Dosed;

  const patchDrug = drugs.get(packedMedication.drugHsId?.toString() ?? "");
  return {
    PatchStatus: hasPatchBeenApplied ? PatchStatus.Applied : undefined,
    TimeRemaining: getPatchTimeRemaining(hasPatchBeenApplied, packedPatientDays, packedMedication, patchDrug!),
    IsNew: !hasPatchBeenApplied
  };
  
  }


// eslint-disable-next-line sonarjs/cognitive-complexity
function getIntervalToNextPatchOrRemoval(index: number, patchAndRemovalLatestFirst: HSPackedMedication[]) {
  let intervalText;

  const nextPatchRemovalTime =
    index > 0 ? DateUtils.toDate(patchAndRemovalLatestFirst[index - 1].doseTimestamp!) : undefined;

  const now = new Date();

  if (nextPatchRemovalTime && now < nextPatchRemovalTime) {
    const intervalDuration = intervalToDuration({ start: now, end: nextPatchRemovalTime });
    intervalText = `${intervalDuration.days! > 0 ? intervalDuration.days + ' day(s)' : ''}`;
  } else {
    intervalText = '0';
  }

  return intervalText;
}

export function getPatchTimeRemaining(
  hasPatchBeenApplied: boolean,
  packedPatientDays: HSPackedPatientDay[],
  currentPackedMedication: HSPackedMedication,
  patchDrug: HSDrug,
) {
  let patchTimeRemaining = '';

  if (!hasPatchBeenApplied) {
    return patchTimeRemaining;
  }

  const patchPackedMedications = packedPatientDays
    .flatMap((p) => p.packedMedications!)
    .filter((pm) => pm.medicationId === currentPackedMedication.medicationId);

  const patchRemovalPackedMedications = packedPatientDays
    .flatMap((p) => p.packedMedications!)
    .filter((pm) => pm.drugHsId === patchDrug?.patchRemovalDrugId);

  const patchAndRemovalLatestFirst = patchPackedMedications
    .concat(patchRemovalPackedMedications)
    .sort((a, b) => DateUtils.compareDateStringsDescending(a.doseTimestamp, b.doseTimestamp));

  if (patchAndRemovalLatestFirst.length > 1) {
    patchAndRemovalLatestFirst.every((pm, index) => {
      if (pm.hsId === currentPackedMedication.hsId) {
        patchTimeRemaining = getIntervalToNextPatchOrRemoval(index, patchAndRemovalLatestFirst);

        return false;
      }
      return true;
    });
  }

  return patchTimeRemaining;
}

function isPatchToSight(activePatch?: HSAdministeredDrug) {
  if (activePatch) {
    const patchAdministeredAt = DateUtils.toDate(activePatch.administeredAt!);
    const scheduledSightingAt = new Date();
    scheduledSightingAt.setHours(7, 0, 0, 0); // default to one hour before 8 am daily

    if (patchAdministeredAt < scheduledSightingAt && new Date() >= scheduledSightingAt) {
      return true;
    }
  }
  return false;
}

interface MedicationInformationProps extends MedicationListProps {
  patchApplication?: PatchApplicationDto;
  packedMedication: HSPackedMedication;
  currentDosedDrug?: HSAdministeredDrug;
  onAdminister: (drugs: HSAdministeredDrug[], doseTimestamp: string) => Promise<EnqueuedDrugCreateData | undefined>;
  roundsStore: ReadonlyMap<string, HSDoseRound>;
  userUtils: UserUtils;
  status?: ReasonCode;
  displayAllActivity?: boolean;
  patchInfo?: PatchInfo;
}

function PatchMedicationInformation(props: MedicationInformationProps) {
  const apiUtils = useApiUtils();
  let appliedText: string|undefined;
  if (!!props.patchApplication) {
    appliedText = getPatchApplicationAppliedText(props.userUtils, props.patchApplication);
  }
  return (
    <MedicationInformationBox
      infoLabel={appliedText}
      previousDosedDrug={ apiUtils.residentDetails.getDosedDrugForPreviousDoseTime(props.patient, props.packedMedication) }
      currentDosedDrug={props.currentDosedDrug}
      scheduledTime={props.packedMedication.doseTimestamp}
      displayAllActivity={props.displayAllActivity ?? false}
      {...props}
    />
  );
}

const getAppliedText = (activePatch: HSAdministeredDrug, patchLocation: PatchLocationDTO, userUtils: UserUtils) => {
  return `Applied to ${patchLocation?.PatchArea.toLowerCase()} ${
    activePatch.administeredBySubjectId
      ? ' by ' + userUtils.getUserFullNameAndRoleFromSubjectId(activePatch.administeredBySubjectId)
      : ''
  } on ${format(DateUtils.toDate(activePatch.administeredAt!), 'dd MMM yyyy')}`;
};
